/**
 * BrightPath - ExpenseTable Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Eye,
  CheckCircle,
} from 'lucide-react';
import type { Expense, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES, CLAIM_STATUS_LABELS } from '../types';
import { formatCurrency, formatDate, cn } from '@/core/lib/utils';
import { useExpenseStore } from '../stores/expenseStore';

type SortField = 'expenseDate' | 'amount' | 'category' | 'createdAt';

interface ExpenseTableProps {
  expenses: Expense[];
  onEdit?: (expense: Expense) => void;
  onDelete?: (expense: Expense) => void;
  onDuplicate?: (expense: Expense) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function ExpenseTable({
  expenses,
  onEdit,
  onDelete,
  onDuplicate,
  isLoading = false,
  emptyMessage = 'No expenses found',
}: ExpenseTableProps) {
  const {
    sortField,
    sortOrder,
    setSort,
    selectedExpenseIds,
    toggleExpenseSelection,
    selectAllExpenses,
    clearSelection,
  } = useExpenseStore();

  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const handleSort = (field: SortField) => {
    setSort(field);
  };

  const handleSelectAll = () => {
    if (selectedExpenseIds.length === expenses.length) {
      clearSelection();
    } else {
      selectAllExpenses(expenses.map((e) => e.id));
    }
  };

  const isAllSelected = expenses.length > 0 && selectedExpenseIds.length === expenses.length;
  const isSomeSelected = selectedExpenseIds.length > 0 && selectedExpenseIds.length < expenses.length;

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-warm-gray/30" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-sage" />
    ) : (
      <ChevronDown className="w-4 h-4 text-sage" />
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-cream-dark" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-t border-warm-gray/10 flex items-center gap-4 px-4">
              <div className="w-5 h-5 bg-cream-dark rounded" />
              <div className="w-32 h-4 bg-cream-dark rounded" />
              <div className="flex-1" />
              <div className="w-24 h-4 bg-cream-dark rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-warm-gray/10 p-12 text-center">
        <p className="text-warm-gray">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-cream-dark/50">
              <th className="w-12 px-4 py-3">
                <button
                  onClick={handleSelectAll}
                  className={cn(
                    'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                    isAllSelected
                      ? 'bg-sage border-sage'
                      : isSomeSelected
                      ? 'bg-sage/50 border-sage'
                      : 'border-warm-gray/30 hover:border-sage'
                  )}
                >
                  {(isAllSelected || isSomeSelected) && (
                    <CheckCircle className="w-3 h-3 text-white" />
                  )}
                </button>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-cream-dark transition-colors"
                onClick={() => handleSort('expenseDate')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                    Date
                  </span>
                  <SortIcon field="expenseDate" />
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                  Description
                </span>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-cream-dark transition-colors"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                    Category
                  </span>
                  <SortIcon field="category" />
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                  Child
                </span>
              </th>
              <th
                className="px-4 py-3 text-right cursor-pointer hover:bg-cream-dark transition-colors"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                    Amount
                  </span>
                  <SortIcon field="amount" />
                </div>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                  Out of Pocket
                </span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-xs font-semibold text-navy uppercase tracking-wider">
                  Status
                </span>
              </th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => {
              const categoryConfig = EXPENSE_CATEGORIES[expense.category];
              const isSelected = selectedExpenseIds.includes(expense.id);
              const claimStatus = expense.claimStatus
                ? CLAIM_STATUS_LABELS[expense.claimStatus]
                : null;

              return (
                <tr
                  key={expense.id}
                  className={cn(
                    'border-t border-warm-gray/10 hover:bg-cream/50 transition-colors',
                    isSelected && 'bg-sage/5'
                  )}
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleExpenseSelection(expense.id)}
                      className={cn(
                        'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                        isSelected
                          ? 'bg-sage border-sage'
                          : 'border-warm-gray/30 hover:border-sage'
                      )}
                    >
                      {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-navy">{formatDate(expense.expenseDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/family/expenses/${expense.id}`}
                      className="text-navy font-medium hover:text-sage transition-colors"
                    >
                      {expense.description}
                    </Link>
                    {expense.vendorName && (
                      <p className="text-xs text-warm-gray mt-0.5">{expense.vendorName}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${categoryConfig.color}15`,
                        color: categoryConfig.color,
                      }}
                    >
                      {categoryConfig.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-warm-gray">
                      {expense.child ? expense.child.firstName : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-navy">
                      {formatCurrency(expense.amount)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        expense.outOfPocket === expense.amount
                          ? 'text-terracotta'
                          : expense.outOfPocket === 0
                          ? 'text-sage'
                          : 'text-navy'
                      )}
                    >
                      {formatCurrency(expense.outOfPocket)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {expense.isTaxDeductible && (
                        <span className="w-2 h-2 rounded-full bg-sage" title="Tax Deductible" />
                      )}
                      {expense.isInsuranceCovered && (
                        <span className="w-2 h-2 rounded-full bg-blue-500" title="Insurance" />
                      )}
                      {claimStatus && (
                        <span
                          className="text-xs"
                          style={{ color: claimStatus.color }}
                          title={claimStatus.label}
                        >
                          {claimStatus.label}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === expense.id ? null : expense.id)}
                        className="p-1 hover:bg-cream rounded transition-colors"
                      >
                        <MoreHorizontal className="w-5 h-5 text-warm-gray" />
                      </button>

                      {openMenuId === expense.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenuId(null)}
                          />
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-warm-gray/10 py-1 z-20">
                            <Link
                              to={`/family/expenses/${expense.id}`}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-cream transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Link>
                            {onEdit && (
                              <button
                                onClick={() => {
                                  onEdit(expense);
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-cream transition-colors w-full text-left"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                            )}
                            {onDuplicate && (
                              <button
                                onClick={() => {
                                  onDuplicate(expense);
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-navy hover:bg-cream transition-colors w-full text-left"
                              >
                                <Copy className="w-4 h-4" />
                                Duplicate
                              </button>
                            )}
                            {onDelete && (
                              <button
                                onClick={() => {
                                  onDelete(expense);
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors w-full text-left"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Selection Summary */}
      {selectedExpenseIds.length > 0 && (
        <div className="px-4 py-3 bg-sage/10 border-t border-sage/20 flex items-center justify-between">
          <span className="text-sm text-sage-dark">
            {selectedExpenseIds.length} expense{selectedExpenseIds.length !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={clearSelection}
            className="text-sm text-sage hover:text-sage-dark transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  );
}

export default ExpenseTable;

